import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCrypto } from '../contexts/CryptoContext';
import { supabase } from '../services/supabaseClient';
import { createVaultItem } from '../services/vaultService';
import { checkinService } from '../services/checkinService';
import { create as createBeneficiary } from '../services/beneficiaryService';
import { generateSeed } from '../services/cryptoService';
import SafeheirLogo from '../components/SafeheirLogo';
import SeedDisplayScreen from '../components/SeedDisplayScreen';
import SeedConfirmationScreen, { pickPositions } from '../components/SeedConfirmationScreen';
import { S, G, BG, inputSt, BtnPrimary, BtnBack, MicroText, ErrBox, IconFrame, IconKey } from '../components/onboardingAtoms';
import { GRACE_OPTIONS, GRACE_DEFAULT } from '../config/checkinConfig';
import { DESIGNATOR_CONSENT_TEXT } from '../config/rgpdConsentConfig';

const IS_DEMO = process.env.REACT_APP_DEMO_MODE === 'true';

const TOTAL = 5;
const FREQ_OPTS = [
  { value: 'weekly',    label: 'Hebdomadaire' },
  { value: 'monthly',   label: 'Mensuel' },
  { value: 'quarterly', label: 'Trimestriel' },
  { value: 'biannual',  label: 'Semestriel' },
  { value: 'vacation',  label: 'Congés exceptionnels' },
  { value: 'demo',      label: 'Démo 2 min' },
];

const visibleFreqOpts = IS_DEMO ? FREQ_OPTS : FREQ_OPTS.filter(f => f.value !== 'demo');
const visibleGrace    = IS_DEMO ? GRACE_OPTIONS : GRACE_OPTIONS.filter(g => g.value !== 0.007);

// ─── Atoms ────────────────────────────────────────────────────────────────────

const ProgressBar = ({ step, onLogout }) => (
  <div style={{
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
    padding: '14px 24px',
    background: 'rgba(10,22,40,0.97)',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    backdropFilter: 'blur(12px)',
  }}>
    <div style={{ maxWidth: 560, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: G, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Configuration</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ fontSize: 12, color: G }}>Étape {step} / {TOTAL}</span>
          <button
            type="button"
            onClick={onLogout}
            style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.3)',
              fontSize: 12, cursor: 'pointer', padding: 0,
              textDecoration: 'underline', textDecorationColor: 'rgba(255,255,255,0.15)',
              fontFamily: 'inherit',
            }}
          >
            Se déconnecter
          </button>
        </div>
      </div>
      <div style={{ height: 3, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
        <div style={{
          height: '100%',
          width: `${(step / TOTAL) * 100}%`,
          background: S,
          borderRadius: 2,
          transition: 'width 0.5s cubic-bezier(0.4,0,0.2,1)',
        }} />
      </div>
    </div>
  </div>
);

const SelBtn = ({ active, onClick, children }) => (
  <button onClick={onClick} style={{
    padding: '9px 16px', borderRadius: 9, cursor: 'pointer',
    fontSize: 14, fontWeight: 500,
    border: active ? `1px solid ${S}` : '1px solid rgba(255,255,255,0.1)',
    background: active ? 'rgba(0,245,160,0.08)' : 'rgba(255,255,255,0.03)',
    color: active ? S : G, transition: 'all 0.2s',
  }}>
    {children}
  </button>
);

const CheckRow = ({ label }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 16px',
    background: 'rgba(0,245,160,0.06)',
    border: '1px solid rgba(0,245,160,0.15)',
    borderRadius: 10,
  }}>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={S} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
    <span style={{ color: '#E8ECF1', fontSize: 14, fontWeight: 500 }}>{label}</span>
  </div>
);

const ShieldAnim = () => (
  <>
    <style>{`@keyframes shPulse{0%,100%{transform:scale(1);filter:drop-shadow(0 0 10px rgba(0,245,160,.5))}50%{transform:scale(1.07);filter:drop-shadow(0 0 22px rgba(0,245,160,.85))}}`}</style>
    <svg width="88" height="88" viewBox="0 0 24 24" fill="none"
      style={{ animation: 'shPulse 2.5s ease-in-out infinite', display: 'block', margin: '0 auto 1.5rem' }}>
      <path d="M12 2L3 6V12C3 17.25 7.25 21.75 12 22C16.75 21.75 21 17.25 21 12V6L12 2Z"
        fill="rgba(0,245,160,0.12)" stroke={S} strokeWidth="1.5" strokeLinejoin="round"/>
      <path d="M8 12l3 3 5-5" stroke={S} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </>
);

// ─── Page ─────────────────────────────────────────────────────────────────────

const PROG_H = 57; // px — hauteur fixe de la barre de progression

const OnboardingPage = () => {
  const { user, refreshUser, logout } = useAuth();
  const { masterKey, unlockVault, initializeMKWithSeed } = useCrypto();
  const navigate   = useNavigate();

  const [step,          setStep]          = useState(1);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState('');
  const [unlockPwd,     setUnlockPwd]     = useState('');
  const [unlockLoading, setUnlockLoading] = useState(false);
  const [unlockError,   setUnlockError]   = useState('');

  // Aiguillage PF-01 CP4 : null = etat MK encore inconnu, false = aucune MK
  // scellee en base (compte neuf), true = MK scellee mais non deverrouillee.
  const [mkPresent,  setMkPresent]  = useState(null);
  const [seedPwd,    setSeedPwd]    = useState('');
  const [seedPhrase, setSeedPhrase] = useState('');
  const [seedPos,    setSeedPos]    = useState([]);
  const [seedStep,   setSeedStep]   = useState('password');
  const [creating,   setCreating]   = useState(false);

  const [vault,     setVault]     = useState({ service: '', lastName: '', firstName: '', username: '', password: '' });
  const [vaultOk,   setVaultOk]   = useState(false);
  const [bene,      setBene]      = useState({ firstName: '', lastName: '', email: '', consent: false });
  const [beneOk,    setBeneOk]    = useState(false);
  const [checkin,   setCheckin]   = useState({ freq: 'monthly', grace: GRACE_DEFAULT, enabled: true });
  const [checkinOk, setCheckinOk] = useState(false);

  const firstName = user?.first_name || user?.full_name?.trim().split(/\s+/)[0] || '';

  // Distingue "pas de MK en base" de "MK non deverrouillee" : masterKey seule
  // ne permet pas de trancher. Repli conservateur sur l'ecran de
  // deverrouillage si la lecture echoue (jamais proposer une creation de MK
  // sur un profil dont on ignore l'etat).
  useEffect(() => {
    if (masterKey !== null || !user?.id) return undefined;

    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('encrypted_mk')
        .eq('id', user.id)
        .single();

      if (cancelled) return;
      if (error) {
        console.error('[SafeHeir] Lecture etat MK impossible:', error);
        setMkPresent(true);
        return;
      }
      setMkPresent(Boolean(data?.encrypted_mk));
    })();

    return () => { cancelled = true; };
  }, [masterKey, user?.id]);

  // Scellement du coffre : seul chemin de creation de MK depuis le retrait du
  // chemin auto-init K13. La seed et le mot de passe sortent de la memoire des
  // que l'UPDATE atomique a reussi. Aucun navigate : setMasterKey fait basculer
  // l'aiguillage sur la branche 3 (wizard) au prochain render.
  const handleSeedConfirmed = async () => {
    if (creating) return;
    setCreating(true);
    try {
      await initializeMKWithSeed(seedPwd, user.id, seedPhrase);
      setSeedPhrase('');
      setSeedPos([]);
      setSeedPwd('');
      setSeedStep('password');
    } catch (e) {
      // On reste sur l'ecran de confirmation : la seed est toujours en memoire,
      // l'utilisateur peut relancer sans avoir a tout renoter. L'erreur est
      // rendue par l'ErrBox du conteneur de la branche 1.
      setError('Impossible de créer votre coffre : ' + (e?.message || 'erreur inconnue'));
    } finally {
      setCreating(false);
    }
  };

  const handleComplete = async () => {
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          onboarding_completed: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) {
        console.error('[SafeHeir] Erreur update onboarding_completed:', error);
      }

      await refreshUser();
      navigate('/dashboard');
    } catch (e) {
      console.error('[SafeHeir] Exception fin onboarding:', e);
      navigate('/dashboard');
    }
  };

  const go = (n) => { setError(''); setStep(n); };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (!unlockPwd || unlockLoading) return;
    setUnlockError('');
    setUnlockLoading(true);
    try {
      await unlockVault(unlockPwd, user.id);
      setUnlockPwd('');
    } catch {
      setUnlockError('Mot de passe incorrect. Réessayez.');
      setUnlockPwd('');
    } finally {
      setUnlockLoading(false);
    }
  };

  const saveVault = async () => {
    if (!vault.service.trim() || !vault.lastName.trim() || !vault.firstName.trim() || !vault.username.trim() || !vault.password.trim()) {
      setError('Veuillez remplir tous les champs.'); return;
    }
    if (vaultOk) { go(3); return; }
    setLoading(true);
    const payload = {
      title:      vault.service,
      identifier: vault.username,
      password:   vault.password,
      url:        '',
      notes:      `Titulaire : ${vault.firstName} ${vault.lastName}`,
      label:      '',
    };
    const r = await createVaultItem({ payload, category: 'misc', masterKey, userId: user.id });
    setLoading(false);
    if (!r.success) { setError(r.error || "Erreur lors de l'enregistrement."); return; }
    setVaultOk(true); go(3);
  };

  const saveBene = async () => {
    if (!bene.firstName.trim() || !bene.lastName.trim() || !bene.email.trim()) { setError("Veuillez remplir le prénom, le nom et l'email."); return; }
    if (!bene.consent) { setError('Veuillez cocher la case de consentement pour continuer.'); return; }
    if (beneOk) { go(4); return; }
    setLoading(true);
    const beneName = `${bene.firstName.trim()} ${bene.lastName.trim()}`;
    const r = await createBeneficiary({
      name: beneName,
      firstName: bene.firstName.trim(),
      lastName: bene.lastName.trim(),
      email: bene.email,
      designator_consent: bene.consent,
    }, masterKey);
    setLoading(false);
    if (!r.success) { setError(r.error || "Erreur lors de l'enregistrement."); return; }
    setBeneOk(true); go(4);
  };

  const saveCheckin = async () => {
    if (checkinOk) { go(5); return; }
    setLoading(true);
    const r = await checkinService.saveConfig({ is_enabled: checkin.enabled, frequency: checkin.freq, grace_period_days: checkin.grace, custom_message: '' });
    setLoading(false);
    if (!r.success) { setError(r.error || "Erreur lors de l'activation."); return; }
    setCheckinOk(true); go(5);
  };

  const WRAP = { maxWidth: 520, margin: '0 auto', padding: '1.5rem 1.5rem 3rem' };

  const pane = (i) => ({
    position: 'absolute', top: 0, left: 0,
    width: '100%', height: '100%', overflowY: 'auto',
    transform: i < step ? 'translateX(-100%)' : i > step ? 'translateX(100%)' : 'translateX(0)',
    transition: 'transform 0.8s ease-in-out',
    pointerEvents: i === step ? 'auto' : 'none',
    background: BG,
  });

  // ── Aiguillage MK — 3 branches (PF-01 CP4) ──────────────────────────────
  // 1. Aucune MK scellée en base (compte neuf) → création du code de
  //    récupération BIP39, seul chemin de scellement depuis le retrait du
  //    chemin auto-init K13 dans unlockVault.
  // 2. MK scellée mais absente de la mémoire (refresh, changement d'appareil)
  //    → écran 0 de déverrouillage (T-K06-01a), inchangé.
  // 3. MK en mémoire → wizard 1-5, inchangé.
  // MKGuard (AppRouter.js) ne protège pas l'onboarding — ce guard est requis ici.
  if (masterKey === null) {

    // État MK encore inconnu : ne rien afficher de trompeur le temps du SELECT.
    if (mkPresent === null) {
      return (
        <>
          <ProgressBar step={step} onLogout={handleLogout} />
          <div style={{ position: 'relative', overflow: 'hidden', height: `calc(100vh - ${PROG_H}px)`, marginTop: PROG_H }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflowY: 'auto', background: BG }}>
              <div style={WRAP}>
                <p style={{ margin: 0, color: G, fontSize: '0.9rem' }}>Vérification de votre coffre…</p>
              </div>
            </div>
          </div>
        </>
      );
    }

    // ── Branche 1 — Création du code de récupération (PF-01 CP4) ─────────
    // Trois sous-écrans : mot de passe (requis par initializeMKWithSeed),
    // affichage des 24 mots, confirmation par retape. La seed n'existe qu'en
    // mémoire React et n'est jamais persistée (DEC-030, DEC-033 Q5).
    if (mkPresent === false) {
      return (
        <>
          <ProgressBar step={step} onLogout={handleLogout} />
          <div style={{ position: 'relative', overflow: 'hidden', height: `calc(100vh - ${PROG_H}px)`, marginTop: PROG_H }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflowY: 'auto', background: BG }}>
              <div style={WRAP}>

                {error && (
                  <div style={{ marginBottom: 16 }}>
                    <ErrBox msg={error} />
                  </div>
                )}

                {seedStep === 'password' && (
                  <>
                    <IconFrame><IconKey /></IconFrame>
                    <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem', fontWeight: 700, color: '#fff' }}>
                      Sécurisons votre coffre
                    </h2>
                    <p style={{ margin: '0 0 1.75rem', color: G, fontSize: '0.9rem', lineHeight: 1.6 }}>
                      Confirmez votre mot de passe. Nous générerons ensuite votre code de
                      récupération, le seul moyen de retrouver votre coffre si vous oubliez
                      ce mot de passe.
                    </p>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (!seedPwd) return;
                        setError('');
                        setSeedPhrase(generateSeed());
                        setSeedPos(pickPositions());
                        setSeedStep('display');
                      }}
                      noValidate
                      style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
                    >
                      <div>
                        <label
                          htmlFor="onboarding-seed-password"
                          style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}
                        >
                          Mot de passe
                        </label>
                        <input
                          id="onboarding-seed-password"
                          name="onboarding-seed-password"
                          type="password"
                          value={seedPwd}
                          onChange={e => setSeedPwd(e.target.value)}
                          placeholder="Votre mot de passe SafeHeir"
                          autoComplete="current-password"
                          style={inputSt}
                        />
                      </div>
                      <BtnPrimary disabled={!seedPwd}>Continuer</BtnPrimary>
                    </form>
                    <MicroText>Votre mot de passe n'est jamais transmis à nos serveurs.</MicroText>
                  </>
                )}

                {seedStep === 'display' && (
                  <SeedDisplayScreen
                    seedPhrase={seedPhrase}
                    onContinue={() => setSeedStep('confirm')}
                    onBack={() => {
                      // La seed est jetée : un retour arrière puis une nouvelle
                      // avance regénère une phrase et un nouveau tirage de
                      // positions (DEC-033 Q5).
                      setSeedPhrase('');
                      setSeedPos([]);
                      setSeedStep('password');
                    }}
                  />
                )}

                {seedStep === 'confirm' && (
                  <SeedConfirmationScreen
                    seedPhrase={seedPhrase}
                    positions={seedPos}
                    onConfirm={handleSeedConfirmed}
                    onBack={() => setSeedStep('display')}
                  />
                )}

              </div>
            </div>
          </div>
        </>
      );
    }

    // ── Branche 2 — Écran 0, déverrouillage MK (T-K06-01a) ────────────────
    // Garantit masterKey déverrouillée avant tout écran (coffre, bénéficiaire).
    return (
      <>
        <ProgressBar step={step} onLogout={handleLogout} />
        <div style={{ position: 'relative', overflow: 'hidden', height: `calc(100vh - ${PROG_H}px)`, marginTop: PROG_H }}>
          <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', overflowY: 'auto', background: BG }}>
            <div style={WRAP}>
              <div style={{
                width: 52, height: 52, borderRadius: 12,
                background: 'rgba(0,245,160,0.08)',
                border: '1px solid rgba(0,245,160,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: S, marginBottom: '1.25rem',
              }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem', fontWeight: 700, color: '#fff' }}>
                Coffre verrouillé
              </h2>
              <p style={{ margin: '0 0 1.75rem', color: G, fontSize: '0.9rem', lineHeight: 1.6 }}>
                Pour des raisons de sécurité, votre coffre se verrouille à chaque changement d'appareil
                ou de navigateur. Saisissez votre mot de passe pour continuer.
              </p>
              <form onSubmit={handleUnlock} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label
                    htmlFor="onboarding-unlock-password"
                    style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}
                  >
                    Mot de passe
                  </label>
                  <input
                    id="onboarding-unlock-password"
                    name="onboarding-unlock-password"
                    type="password"
                    value={unlockPwd}
                    onChange={e => { setUnlockPwd(e.target.value); setUnlockError(''); }}
                    placeholder="Votre mot de passe SafeHeir"
                    autoComplete="current-password"
                    disabled={unlockLoading}
                    style={{
                      ...inputSt,
                      borderColor: unlockError ? 'rgba(239,68,68,0.6)' : 'rgba(255,255,255,0.1)',
                    }}
                  />
                  {unlockError && (
                    <p style={{ margin: '6px 0 0', fontSize: 13, color: '#f87171' }} role="alert">
                      {unlockError}
                    </p>
                  )}
                </div>
                <BtnPrimary
                  loading={unlockLoading}
                  disabled={!unlockPwd}
                >
                  Déverrouiller mon coffre
                </BtnPrimary>
              </form>
              <MicroText>Votre mot de passe n'est jamais transmis à nos serveurs.</MicroText>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Branche 3 — MK en mémoire : wizard 1-5 ──────────────────────────────
  return (
    <>
      <ProgressBar step={step} onLogout={handleLogout} />

      <div style={{ position: 'relative', overflow: 'hidden', height: `calc(100vh - ${PROG_H}px)`, marginTop: PROG_H }}>

        {/* ── Écran 1 — Bienvenue ──────────────────────────────────────────── */}
        <div style={pane(1)}>
          <div style={{ ...WRAP, textAlign: 'center', paddingTop: '3rem' }}>
            <SafeheirLogo size="lg" />
            <h1 style={{ margin: '2rem 0 0.75rem', fontSize: '2rem', fontWeight: 700, color: '#fff', lineHeight: 1.15 }}>
              Bienvenue{firstName ? `, ${firstName}` : ''}.
            </h1>
            <p style={{ margin: '0 0 2.5rem', color: G, fontSize: '1rem', lineHeight: 1.65 }}>
              Votre compte est créé. Nous allons vous guider en 4 étapes simples.
            </p>
            <BtnPrimary onClick={() => go(2)}>Commencer ma configuration →</BtnPrimary>
            <MicroText>Vos données sont chiffrées côté client avant transmission.</MicroText>
          </div>
        </div>

        {/* ── Écran 2 — Coffre-fort ────────────────────────────────────────── */}
        <div style={pane(2)}>
          <div style={WRAP}>
            <BtnBack onClick={() => go(1)} />
            <h2 style={{ margin: '0.25rem 0 0.5rem', fontSize: '1.5rem', fontWeight: 700, color: '#fff' }}>
              Ajoutez votre premier accès sécurisé
            </h2>
            <p style={{ margin: '0 0 1.75rem', color: G, fontSize: '0.9rem', lineHeight: 1.6 }}>
              Commencez par un seul compte — vous pourrez en ajouter d'autres plus tard.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: '1.5rem' }}>
              <ErrBox msg={error} />
              <div>
                <label style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}>Service (ex : Gmail, Banque en ligne…)</label>
                <input type="text" value={vault.service}
                  onChange={e => setVault(v => ({ ...v, service: e.target.value }))}
                  placeholder="Gmail" style={inputSt} />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}>Nom</label>
                  <input type="text" value={vault.lastName}
                    onChange={e => setVault(v => ({ ...v, lastName: e.target.value }))}
                    placeholder="Dupont" style={inputSt} autoComplete="family-name" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}>Prénom</label>
                  <input type="text" value={vault.firstName}
                    onChange={e => setVault(v => ({ ...v, firstName: e.target.value }))}
                    placeholder="Marie" style={inputSt} autoComplete="given-name" />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}>Identifiant ou adresse email</label>
                <input type="text" value={vault.username}
                  onChange={e => setVault(v => ({ ...v, username: e.target.value }))}
                  placeholder="votre@email.com" style={inputSt} autoComplete="off" />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}>Mot de passe</label>
                <input type="password" value={vault.password}
                  onChange={e => setVault(v => ({ ...v, password: e.target.value }))}
                  placeholder="••••••••" style={inputSt} autoComplete="new-password" />
              </div>
            </div>
            <BtnPrimary onClick={saveVault} loading={loading}
              disabled={!vault.service || !vault.lastName || !vault.firstName || !vault.username || !vault.password}>
              Enregistrer et continuer →
            </BtnPrimary>
            <MicroText>Ce mot de passe est chiffré localement. Personne d'autre ne peut le lire.</MicroText>
          </div>
        </div>

        {/* ── Écran 3 — Bénéficiaire ───────────────────────────────────────── */}
        <div style={pane(3)}>
          <div style={WRAP}>
            <BtnBack onClick={() => go(2)} />

            <h2 style={{ margin: '0.25rem 0 0.5rem', fontSize: '1.5rem', fontWeight: 700, color: '#fff' }}>
              Désignez votre successeur numérique
            </h2>
            <p style={{ margin: '0 0 1.75rem', color: G, fontSize: '0.9rem', lineHeight: 1.6 }}>
              Cette personne sera contactée uniquement si vous ne répondez pas à vos check-ins.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: '1.5rem' }}>
              <ErrBox msg={error} />
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}>Prénom *</label>
                  <input type="text" value={bene.firstName}
                    onChange={e => setBene(b => ({ ...b, firstName: e.target.value }))}
                    placeholder="ex: Marie" style={inputSt} autoComplete="given-name" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}>Nom *</label>
                  <input type="text" value={bene.lastName}
                    onChange={e => setBene(b => ({ ...b, lastName: e.target.value }))}
                    placeholder="ex: Dupont" style={inputSt} autoComplete="family-name" />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 13, color: G, marginBottom: 6 }}>Adresse email</label>
                <input type="email" value={bene.email}
                  onChange={e => setBene(b => ({ ...b, email: e.target.value }))}
                  placeholder="marie@email.com" style={inputSt} />
              </div>
              <label style={{
                display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                background: 'rgba(255,255,255,0.03)',
                border: `1px solid ${bene.consent ? 'rgba(0,245,160,0.25)' : 'rgba(255,255,255,0.07)'}`,
                borderRadius: 10, padding: '12px 14px',
                transition: 'border-color 0.2s',
              }}>
                <input type="checkbox" checked={bene.consent}
                  onChange={e => setBene(b => ({ ...b, consent: e.target.checked }))}
                  style={{ marginTop: 2, accentColor: S, flexShrink: 0 }} />
                <span style={{ fontSize: 13, color: '#B0BEC5', lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                  {DESIGNATOR_CONSENT_TEXT}
                </span>
              </label>
            </div>
            <BtnPrimary onClick={saveBene} loading={loading}
              disabled={!bene.firstName || !bene.lastName || !bene.email || !bene.consent}>
              Désigner et continuer →
            </BtnPrimary>
            <MicroText>Vous pourrez modifier ou supprimer ce choix à tout moment.</MicroText>
          </div>
        </div>

        {/* ── Écran 4 — Check-in ───────────────────────────────────────────── */}
        <div style={pane(4)}>
          <div style={WRAP}>
            <BtnBack onClick={() => go(3)} />
            <h2 style={{ margin: '0.25rem 0 0.5rem', fontSize: '1.5rem', fontWeight: 700, color: '#fff' }}>
              Configurez votre signal de vie
            </h2>
            <p style={{ margin: '0 0 1.75rem', color: G, fontSize: '0.9rem', lineHeight: 1.6 }}>
              SafeHeir vous enverra un rappel. Sans réponse, vos proches sont alertés.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginBottom: '1.5rem' }}>
              <ErrBox msg={error} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#C8D4E0', marginBottom: 10 }}>Fréquence de vérification</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {visibleFreqOpts.map(f => (
                    <SelBtn key={f.value} active={checkin.freq === f.value}
                      onClick={() => setCheckin(c => ({ ...c, freq: f.value }))}>
                      {f.label}
                    </SelBtn>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#C8D4E0', marginBottom: 10 }}>Délai avant alerte (après l'échéance)</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {visibleGrace.map(g => (
                    <SelBtn key={g.value} active={checkin.grace === g.value}
                      onClick={() => setCheckin(c => ({ ...c, grace: g.value }))}>
                      {g.label}
                    </SelBtn>
                  ))}
                </div>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}>
                <div
                  onClick={() => setCheckin(c => ({ ...c, enabled: !c.enabled }))}
                  style={{
                    width: 48, height: 26, borderRadius: 13,
                    position: 'relative', cursor: 'pointer', flexShrink: 0,
                    background: checkin.enabled ? S : 'rgba(255,255,255,0.1)',
                    transition: 'background 0.3s',
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 3,
                    left: checkin.enabled ? 25 : 3,
                    width: 20, height: 20, borderRadius: '50%',
                    background: '#fff', transition: 'left 0.3s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  }} />
                </div>
                <span style={{ fontSize: 14, color: '#E8ECF1' }}>Activer ma protection automatique</span>
              </label>
            </div>
            <BtnPrimary onClick={saveCheckin} loading={loading}>Activer et continuer →</BtnPrimary>
            <MicroText>Vous pouvez suspendre ou modifier cette fréquence à tout moment.</MicroText>
          </div>
        </div>

        {/* ── Écran 5 — Confirmation ───────────────────────────────────────── */}
        <div style={pane(5)}>
          <div style={{ ...WRAP, textAlign: 'center', paddingTop: '2rem' }}>
            <ShieldAnim />
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.875rem', fontWeight: 700, color: '#fff' }}>
              Vous êtes protégé.
            </h2>
            <p style={{ margin: '0 0 2rem', color: G, fontSize: '0.9rem' }}>
              Votre configuration SafeHeir est complète.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: '2rem', textAlign: 'left' }}>
              <CheckRow label="Coffre-fort configuré" />
              <CheckRow label="Bénéficiaire désigné" />
              <CheckRow label="Protection automatique activée" />
            </div>
            <BtnPrimary onClick={handleComplete}>
              Accéder à mon tableau de bord →
            </BtnPrimary>
            <MicroText>SafeHeir veille. Vous pouvez compléter votre profil à votre rythme.</MicroText>
          </div>
        </div>

      </div>
    </>
  );
};

export default OnboardingPage;
